import type { CardDataSchema, CardSchemaProperty, CardValidationIssue } from './types';

function valueAt(data: Record<string, unknown>, path: string): unknown {
	return path.split('.').reduce<unknown>((value, key) => {
		if (!value || typeof value !== 'object') return undefined;
		return (value as Record<string, unknown>)[key];
	}, data);
}

function validateProperty(
	value: unknown,
	property: CardSchemaProperty,
	path: string,
): CardValidationIssue[] {
	const issues: CardValidationIssue[] = [];
	if (value == null) return issues;
	if (property.type === 'string' && typeof value !== 'string')
		issues.push({ path, message: 'Must be text' });
	if ((property.type === 'number' || property.type === 'integer') && typeof value !== 'number')
		issues.push({ path, message: 'Must be a number' });
	if (property.type === 'object' && (typeof value !== 'object' || Array.isArray(value)))
		issues.push({ path, message: 'Must be an object' });
	if (typeof value === 'string' && property.minLength && value.trim().length < property.minLength)
		issues.push({ path, message: 'Required' });
	if (typeof value === 'number' && property.minimum != null && value < property.minimum)
		issues.push({ path, message: `Must be at least ${property.minimum}` });
	if (typeof value === 'number' && property.maximum != null && value > property.maximum)
		issues.push({ path, message: `Must be at most ${property.maximum}` });
	if (property.enum && !property.enum.includes(value))
		issues.push({ path, message: 'Choose a supported value' });
	if (property.type === 'object' && value && property.properties) {
		for (const required of property.required ?? []) {
			const nested = (value as Record<string, unknown>)[required];
			if (nested == null || (typeof nested === 'string' && !nested.trim()))
				issues.push({ path: `${path}.${required}`, message: 'Required' });
		}
		for (const [key, nestedSchema] of Object.entries(property.properties)) {
			issues.push(
				...validateProperty(
					(value as Record<string, unknown>)[key],
					nestedSchema,
					`${path}.${key}`,
				),
			);
		}
	}
	return issues;
}

export function validateCardData(
	schema: CardDataSchema,
	data: Record<string, unknown>,
): CardValidationIssue[] {
	const issues: CardValidationIssue[] = [];
	for (const path of schema.required ?? []) {
		const value = valueAt(data, path);
		if (value == null || (typeof value === 'string' && !value.trim()))
			issues.push({ path, message: 'Required' });
	}
	for (const [path, property] of Object.entries(schema.properties)) {
		issues.push(...validateProperty(valueAt(data, path), property, path));
	}
	return issues;
}
